/**
 * Record-list report engine.
 *
 * Salesforce-style row-level list reports ("give me every open opportunity with
 * these 20 columns, grouped by region, sorted by stage duration") are a
 * different shape from the dashboard blocks in `customBlocks.tsx`: instead of a
 * hardcoded column set, the block is driven by a *spec* the user can describe in
 * natural language and then refine.
 *
 * The spec is serialized into the existing `SavedReport.customBlocks: string[]`
 * as `list:<base64 json>` so saved reports keep working without a schema change.
 */

import { companies as dummyCompanies, deals as dummyDeals, people as dummyPeople, pipelineStages } from "@/data/dummy";
import { PRODUCTION_UNSUPPORTED_FIELDS } from "@/lib/reports/record-list-mapping";

export type RecordObject = "deals" | "companies" | "people";

export type FieldType = "text" | "number" | "currency" | "date" | "select";

export interface RecordFieldDef {
  key: string;
  label: string;
  type: FieldType;
  /** Custom (Data Model) fields are flagged so the UI can group them. */
  custom?: boolean;
  /** Enumerated values for select-type custom fields, for the filter value picker. */
  options?: string[];
}

export type FilterOp = "is" | "is not" | "contains" | "greater than" | "less than" | "is not empty" | "is empty";

export interface RecordFilter {
  field: string;
  op: FilterOp;
  values: string[];
}

export interface RecordListSpec {
  object: RecordObject;
  columns: string[];
  filters: RecordFilter[];
  groupBy?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  /** Heading shown above the table. */
  title: string;
  /** The natural-language sentence this spec was derived from. */
  prompt?: string;
}

// ---------- Field catalogs ----------

const DEAL_FIELDS: RecordFieldDef[] = [
  { key: "name", label: "Deal Name", type: "text" },
  { key: "company", label: "Account Name", type: "text" },
  { key: "stage", label: "Stage", type: "select" },
  { key: "amount", label: "Amount", type: "currency" },
  { key: "closeDate", label: "Close Date", type: "date" },
  { key: "owner", label: "Owner", type: "select" },
  { key: "forecastCategory", label: "Forecast Category", type: "select" },
  { key: "probability", label: "Probability", type: "number" },
  { key: "contacts", label: "Contacts", type: "text" },
  { key: "billing", label: "Billing", type: "select" },
  { key: "createdDate", label: "Created Date", type: "date" },
  { key: "createdBy", label: "Created By", type: "select" },
  { key: "stageEnteredDate", label: "Stage Entered", type: "date" },
  { key: "stageDuration", label: "Stage Duration (days)", type: "number" },
  { key: "age", label: "Age (days)", type: "number" },
  { key: "lastEmail", label: "Last Email", type: "date" },
  { key: "lastMeeting", label: "Last Meeting", type: "date" },
  { key: "nextMeeting", label: "Next Meeting", type: "date" },
  { key: "industry", label: "Industry", type: "select" },
  { key: "country", label: "Billing Country", type: "select" },
  { key: "region", label: "Region", type: "select" },
];

const COMPANY_FIELDS: RecordFieldDef[] = [
  { key: "name", label: "Account Name", type: "text" },
  { key: "website", label: "Website", type: "text" },
  { key: "industry", label: "Industry", type: "select" },
  { key: "employees", label: "Employees", type: "text" },
  { key: "revenue", label: "Revenue", type: "text" },
  { key: "owner", label: "Owner", type: "select" },
  { key: "city", label: "Billing City", type: "text" },
  { key: "stateProvince", label: "Billing State", type: "text" },
  { key: "country", label: "Billing Country", type: "select" },
  { key: "postalCode", label: "Billing Zip", type: "text" },
  { key: "continent", label: "Region", type: "select" },
  { key: "companyType", label: "Type", type: "select" },
  { key: "funding", label: "Funding", type: "text" },
  { key: "foundingYear", label: "Founded", type: "number" },
  { key: "phone", label: "Phone", type: "text" },
  { key: "primaryContact", label: "Primary Contact", type: "text" },
  { key: "primaryContactEmail", label: "Primary Contact Email", type: "text" },
  { key: "openDeals", label: "Open Deals", type: "number" },
  { key: "openPipeline", label: "Open Pipeline", type: "currency" },
  { key: "description", label: "Description", type: "text" },
];

const PEOPLE_FIELDS: RecordFieldDef[] = [
  { key: "name", label: "Name", type: "text" },
  { key: "title", label: "Title", type: "text" },
  { key: "company", label: "Account Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "phone", label: "Phone", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "owner", label: "Owner", type: "select" },
  { key: "createdDate", label: "Created Date", type: "date" },
  { key: "lastEmail", label: "Last Email", type: "date" },
  { key: "lastMeeting", label: "Last Meeting", type: "date" },
  { key: "nextMeeting", label: "Next Meeting", type: "date" },
];

export const OBJECT_LABEL: Record<RecordObject, string> = {
  deals: "Deals",
  companies: "Companies",
  people: "People",
};

/**
 * Options shared by the catalog helpers.
 *
 * `production: true` drops the fields no live query can fill (see
 * `PRODUCTION_UNSUPPORTED_FIELDS`), so the column picker, the group/sort menus and the
 * filter field list only ever offer columns that will actually have values. The default
 * is the full preview catalog, which keeps every existing caller unchanged.
 */
export interface FieldCatalogOptions {
  production?: boolean;
}

export function standardFields(object: RecordObject, opts: FieldCatalogOptions = {}): RecordFieldDef[] {
  const all = object === "companies" ? COMPANY_FIELDS : object === "people" ? PEOPLE_FIELDS : DEAL_FIELDS;
  if (!opts.production) return all;
  const unsupported = PRODUCTION_UNSUPPORTED_FIELDS[object];
  return all.filter(f => !unsupported.has(f.key));
}

export function fieldCatalog(
  object: RecordObject,
  custom: RecordFieldDef[] = [],
  opts: FieldCatalogOptions = {},
): RecordFieldDef[] {
  const standard = standardFields(object, opts);
  const seen = new Set(standard.map(f => f.key));
  return [...standard, ...custom.filter(f => !seen.has(f.key))];
}

export function findField(
  object: RecordObject,
  key: string,
  custom: RecordFieldDef[] = [],
  opts: FieldCatalogOptions = {},
): RecordFieldDef | undefined {
  return fieldCatalog(object, custom, opts).find(f => f.key === key);
}

// ---------- Rows ----------

const REGION_BY_COUNTRY: Record<string, string> = {
  "United States": "North America",
  Canada: "North America",
  Australia: "APAC",
  Singapore: "APAC",
  Japan: "APAC",
  "United Kingdom": "EMEA",
  Germany: "EMEA",
  France: "EMEA",
  Poland: "EMEA",
};

const daysBetween = (from: string | undefined, to = new Date()): number | undefined => {
  if (!from || from === "-") return undefined;
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return undefined;
  return Math.max(0, Math.round((to.getTime() - d.getTime()) / 86_400_000));
};

const stagePct = (stage: string) => pipelineStages.find(s => s.name === stage)?.pct ?? 0;

/** Continent names in the dummy data use different vocabulary than sales regions. */
const CONTINENT_TO_REGION: Record<string, string> = {
  Europe: "EMEA",
  Africa: "EMEA",
  Asia: "APAC",
  Oceania: "APAC",
  "South America": "LATAM",
};

const regionFor = (country?: string, continent?: string): string =>
  (country && REGION_BY_COUNTRY[country]) ||
  (continent && (CONTINENT_TO_REGION[continent] ?? continent)) ||
  "-";

export function buildRows(object: RecordObject): Record<string, any>[] {
  if (object === "people") {
    return dummyPeople.map(p => ({ ...p }));
  }
  if (object === "companies") {
    return dummyCompanies.map(c => {
      const related = dummyDeals.filter(d => d.company === c.name);
      const open = related.filter(d => d.stage !== "Closed Won" && d.stage !== "Closed Lost");
      const contact = dummyPeople.find(p => p.company === c.name);
      return {
        ...c,
        primaryContact: contact?.name ?? "-",
        primaryContactEmail: contact?.email ?? "-",
        openDeals: open.length,
        openPipeline: open.reduce((s, d) => s + (d.amount || 0), 0),
      };
    });
  }
  return dummyDeals.map(d => {
    const account = dummyCompanies.find(c => c.name === d.company);
    return {
      ...d,
      probability: stagePct(d.stage),
      stageDuration: daysBetween((d as any).stageEnteredDate) ?? 0,
      age: daysBetween((d as any).createdDate) ?? 0,
      industry: account?.industry ?? "-",
      country: account?.country ?? "-",
      region: regionFor(account?.country, account?.continent),
    };
  });
}

/** Distinct values for a field — powers the filter value pickers. */
export function fieldValues(object: RecordObject, key: string): string[] {
  const vals = new Set<string>();
  for (const row of buildRows(object)) {
    const v = row[key];
    if (v === undefined || v === null || v === "" || v === "-") continue;
    vals.add(String(v));
  }
  return [...vals].sort();
}

// ---------- Filtering / sorting / grouping ----------

const toNumber = (v: any): number => {
  if (typeof v === "number") return v;
  const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const compareValues = (a: any, b: any, type: FieldType): number => {
  if (type === "number" || type === "currency") return toNumber(a) - toNumber(b);
  if (type === "date") {
    const da = new Date(a).getTime();
    const db = new Date(b).getTime();
    if (Number.isNaN(da) && Number.isNaN(db)) return 0;
    if (Number.isNaN(da)) return 1;
    if (Number.isNaN(db)) return -1;
    return da - db;
  }
  return String(a ?? "").localeCompare(String(b ?? ""));
};

function matches(row: Record<string, any>, filter: RecordFilter, type: FieldType): boolean {
  const raw = row[filter.field];
  const str = String(raw ?? "").trim();
  switch (filter.op) {
    case "is":
      return filter.values.some(v => str.toLowerCase() === v.toLowerCase());
    case "is not":
      return !filter.values.some(v => str.toLowerCase() === v.toLowerCase());
    case "contains":
      return filter.values.some(v => str.toLowerCase().includes(v.toLowerCase()));
    case "greater than":
      return type === "date"
        ? new Date(str).getTime() > new Date(filter.values[0]).getTime()
        : toNumber(raw) > toNumber(filter.values[0]);
    case "less than":
      return type === "date"
        ? new Date(str).getTime() < new Date(filter.values[0]).getTime()
        : toNumber(raw) < toNumber(filter.values[0]);
    case "is empty":
      return str === "" || str === "-";
    case "is not empty":
      return str !== "" && str !== "-";
    default:
      return true;
  }
}

export interface RecordGroup {
  key: string;
  rows: Record<string, any>[];
}

export interface RecordListResult {
  rows: Record<string, any>[];
  groups: RecordGroup[];
  columns: RecordFieldDef[];
}

/**
 * Apply a spec to a row set.
 *
 * `sourceRows` lets production hand in rows mapped from live entity queries
 * (`useRecordListRows`); it defaults to the seeded preview rows so the engine, and
 * every existing caller and test, behaves exactly as before when nothing is passed.
 */
export function runRecordList(
  spec: RecordListSpec,
  custom: RecordFieldDef[] = [],
  sourceRows?: Record<string, any>[],
): RecordListResult {
  const catalog = fieldCatalog(spec.object, custom);
  const typeOf = (key: string): FieldType => catalog.find(f => f.key === key)?.type ?? "text";

  let rows = sourceRows ?? buildRows(spec.object);
  for (const f of spec.filters) {
    if (f.op !== "is empty" && f.op !== "is not empty" && f.values.length === 0) continue;
    rows = rows.filter(r => matches(r, f, typeOf(f.field)));
  }

  if (spec.sortBy) {
    const dir = spec.sortDir === "desc" ? -1 : 1;
    const type = typeOf(spec.sortBy);
    rows = [...rows].sort((a, b) => compareValues(a[spec.sortBy!], b[spec.sortBy!], type) * dir);
  }

  const columns = spec.columns
    .map(key => catalog.find(f => f.key === key) ?? { key, label: key, type: "text" as FieldType })
    .filter(Boolean);

  let groups: RecordGroup[] = [{ key: "", rows }];
  if (spec.groupBy) {
    const map = new Map<string, Record<string, any>[]>();
    for (const r of rows) {
      const k = String(r[spec.groupBy] ?? "—") || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    groups = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([key, rs]) => ({ key, rows: rs }));
  }

  return { rows, groups, columns };
}

export function formatCell(value: any, type: FieldType): string {
  if (value === undefined || value === null || value === "") return "-";
  if (type === "currency") {
    const n = toNumber(value);
    return n >= 1000 ? `$${Math.round(n).toLocaleString()}` : `$${n}`;
  }
  if (type === "number") return String(toNumber(value).toLocaleString());
  return String(value);
}

export function sumColumn(rows: Record<string, any>[], key: string): number {
  return rows.reduce((s, r) => s + toNumber(r[key]), 0);
}

// ---------- Block-id serialization ----------

const PREFIX = "list:";

export const isRecordListBlockId = (id: string) => id.startsWith(PREFIX);

export function encodeRecordListBlock(spec: RecordListSpec): string {
  const json = JSON.stringify(spec);
  // btoa is byte-oriented; encodeURIComponent keeps unicode titles safe.
  return PREFIX + btoa(unescape(encodeURIComponent(json)));
}

export function decodeRecordListBlock(id: string): RecordListSpec | null {
  if (!isRecordListBlockId(id)) return null;
  try {
    const json = decodeURIComponent(escape(atob(id.slice(PREFIX.length))));
    const spec = JSON.parse(json) as RecordListSpec;
    if (!spec || !spec.object || !Array.isArray(spec.columns)) return null;
    return { filters: [], ...spec };
  } catch {
    return null;
  }
}
